// §4.2 — the service registry: every kernel service is constructed exactly once, at boot,
// and reached by a typed token. Tokens rather than string keys keep `resolve` type-safe
// without an `any` and without cross-module declaration merging.
import { createBus, type Bus } from './bus';

declare const TOKEN_BRAND: unique symbol;

export interface ServiceToken<T> {
  readonly name: string;
  /** Phantom field — carries `T` through `resolve` without existing at runtime. */
  readonly [TOKEN_BRAND]?: (value: T) => T;
}

export function serviceToken<T>(name: string): ServiceToken<T> {
  return { name };
}

export type ServiceFactory<T> = (kernel: Kernel) => T;

export interface Kernel {
  readonly bus: Bus;
  /** Register a service factory. Registering the same token twice is an error. */
  provide<T>(token: ServiceToken<T>, factory: ServiceFactory<T>): void;
  /** Resolve a service, constructing it on first use. Always the same instance after that. */
  resolve<T>(token: ServiceToken<T>): T;
  /** Construct every registered service. Called once at boot. */
  start(): void;
  /** Registered token names, in registration order — App Inspector reads this. */
  serviceNames(): string[];
}

export function createKernel(bus: Bus = createBus()): Kernel {
  const factories = new Map<string, ServiceFactory<unknown>>();
  const instances = new Map<string, unknown>();
  const constructing = new Set<string>();

  const kernel: Kernel = {
    bus,

    provide(token, factory) {
      if (factories.has(token.name)) {
        throw new Error(`kernel: service '${token.name}' is already registered`);
      }
      factories.set(token.name, factory as ServiceFactory<unknown>);
    },

    resolve<T>(token: ServiceToken<T>): T {
      if (instances.has(token.name)) return instances.get(token.name) as T;

      const factory = factories.get(token.name);
      if (!factory) throw new Error(`kernel: no service registered for '${token.name}'`);

      if (constructing.has(token.name)) {
        throw new Error(
          `kernel: circular construction of '${token.name}' — services must talk over the bus (§4.2)`,
        );
      }
      constructing.add(token.name);
      try {
        const instance = factory(kernel) as T;
        instances.set(token.name, instance);
        return instance;
      } finally {
        constructing.delete(token.name);
      }
    },

    start() {
      for (const name of factories.keys()) {
        kernel.resolve(serviceToken<unknown>(name));
      }
    },

    serviceNames() {
      return [...factories.keys()];
    },
  };

  return kernel;
}
